/**
 * `.tlog` 的读取与回放调度。
 *
 * 读取端的核心指标不是"能解出记录"，而是**能从损坏处重新同步**：真实文件里既有
 * 上电时的垃圾字节，也有被截断的尾巴，还有同一份文件里 v1 与 v2 混着出现的情况。
 * 因此这里的纪律是"逐条切分 + 记账"：
 *
 *   - 起点必须是【8 字节时间戳 + STX】，否则立刻进入重同步，并把丢掉的字节数记下来；
 *   - 长度只能由 MAVLink 自己算（含 v2 的 13 字节签名块）；
 *   - 数据不够时**等一下**，不能当成坏数据丢掉（否则切分位置会越走越偏）；
 *   - 帧的 CRC 只用于**诊断与重同步判定**，不影响"这条记录是否发出去"：
 *     `.tlog` 是字节归档，写进去什么就得读出什么，CRC 坏掉本身也是要如实报告的事实。
 *
 * 回放部分把"时间"这件事做成可注入的（`ReplayClock`），于是变速、暂停、跳转都能在
 * 单元测试里用虚拟时钟精确断言 —— 回放最容易写错的地方正是"相对时距"，
 * 而相对时距用真实时钟根本测不准。
 */
import {
  MAVLINK_CORE_HEADER_LENGTH_V1,
  MAVLINK_CORE_HEADER_LENGTH_V2,
  crc16X25,
  crcAccumulate,
  messageById,
} from '@hgc/mavlink';

import {
  TLOG_RECORD_TIMESTAMP_BYTES,
  frameLengthOf,
  hasFrameStx,
  readTimestampUs,
} from './record';

/** 从 `.tlog` 里解出的一条记录 */
export interface TlogRecord {
  /** Unix 纪元微秒时间戳 */
  readonly timestampUs: number;
  /** 原始 MAVLink 帧字节（独立副本，下次 `push` 之后依然有效） */
  readonly frame: Uint8Array;
  /** 记录起点（时间戳第一个字节）在文件中的偏移 */
  readonly offset: number;
}

/** 读取统计。这些数字要直接进界面上的"文件诊断"，因此每一项都必须能被解释。 */
export interface TlogStats {
  /** 成功解出的记录条数 */
  records: number;
  /** 起点对不上的记录次数（每次触发一次重同步） */
  badRecords: number;
  /** 重同步时被跳过的字节数 */
  droppedBytes: number;
  /** CRC 与帧内不一致的帧数。文件里的帧来自链路，链路上坏了才会这样。 */
  crcErrors: number;
  /** 消息表里没有的消息的帧数（固件比本地面站新） */
  unknownMessages: number;
  firstTimestampUs: number | null;
  lastTimestampUs: number | null;
  /** 流结束时尾部剩余、无法构成完整记录的字节数 */
  incompleteBytes: number;
  /** 读入的总字节数 */
  bytesRead: number;
}

/** 被截断的尾部。UI 应当提示"文件末尾有 N 字节不完整"，而不是假装文件是完整的。 */
export interface TlogIncomplete {
  readonly bytes: number;
  /** 尾部已经够 8 字节时能解出的时间戳；不足 8 字节时为 null */
  readonly timestampUs: number | null;
  /** 前 16 字节的十六进制，便于一眼看出是"半个帧"还是纯垃圾 */
  readonly preview: string;
}

export interface TlogReaderOptions {
  /** 每解出一条记录就调用一次。流式消费用它可以不自己收集数组。 */
  readonly onRecord?: (record: TlogRecord) => void;
  /**
   * 重同步时是否用 CRC 校验候选位置（默认 true）。
   *
   * 垃圾字节里凑巧出现 0xFD 是常事，只看 STX 会把切分点定到垃圾里，
   * 于是后面成片的真数据被"解成"乱码。CRC 让候选位置几乎不可能被蒙对。
   */
  readonly resyncByCrc?: boolean;
}

type CrcState = 'ok' | 'bad' | 'unknown';

/**
 * 判断一帧的 CRC 是否成立。
 *
 * `guess` 为真时会穷举 CRC_EXTRA —— 只用在重同步上：消息表里没有的消息算不出 CRC_EXTRA，
 * 而重同步的候选位置**必须**能被验证，否则垃圾里的 0xFD 会被当成记录起点。
 */
function frameCrcState(frame: Uint8Array, guess: boolean): CrcState {
  const version = frame[0] === 0xfd ? 2 : 1;
  const headerLength = version === 2 ? MAVLINK_CORE_HEADER_LENGTH_V2 : MAVLINK_CORE_HEADER_LENGTH_V1;
  const payloadLength = frame[1] ?? 0;
  const crcStart = 1;
  const crcEnd = crcStart + headerLength + payloadLength;
  if (crcEnd + 2 > frame.length) return 'bad';

  const messageId =
    version === 2
      ? (frame[7] ?? 0) | ((frame[8] ?? 0) << 8) | ((frame[9] ?? 0) << 16)
      : (frame[5] ?? 0);
  const expected = (frame[crcEnd] ?? 0) | ((frame[crcEnd + 1] ?? 0) << 8);

  const definition = messageById(messageId);
  const base = crc16X25(frame, crcStart, crcEnd);
  if (definition !== undefined) {
    return crcAccumulate(base, definition.crcExtra) === expected ? 'ok' : 'bad';
  }
  if (!guess) return 'unknown';
  for (let candidate = 0; candidate < 256; candidate += 1) {
    if (crcAccumulate(base, candidate) === expected) return 'ok';
  }
  return 'bad';
}

function previewHex(bytes: Uint8Array, offset: number, length: number, limit = 16): string {
  const end = Math.min(offset + length, offset + limit);
  const parts: string[] = [];
  for (let index = offset; index < end; index += 1) {
    parts.push((bytes[index] ?? 0).toString(16).padStart(2, '0'));
  }
  return parts.join(' ');
}

/**
 * `.tlog` 流式解析器。
 *
 * 可以以任意字节边界分片喂入；只有当整条记录到齐时才产出记录，
 * 因此跨越分片的记录不会被打断。
 */
export class TlogReader {
  readonly stats: TlogStats = {
    records: 0,
    badRecords: 0,
    droppedBytes: 0,
    crcErrors: 0,
    unknownMessages: 0,
    firstTimestampUs: null,
    lastTimestampUs: null,
    incompleteBytes: 0,
    bytesRead: 0,
  };

  private readonly options: TlogReaderOptions;
  private buffer = new Uint8Array(8192);
  private length = 0;
  /** `buffer[0]` 在文件中的绝对偏移 */
  private base = 0;
  /** 缓冲区里已消费到的下标（下一次从它开始切分） */
  private cursor = 0;
  private tail: TlogIncomplete | null = null;
  /**
   * 最近一次已经记过账的"起点对不上"的绝对偏移。
   *
   * `drain()` 会在 `push` 与 `end` 各跑一次，同一次损坏如果每次都计数，
   * 统计里的"坏记录数"就会翻倍 —— 而这个数字是要给人看诊断结论的。
   */
  private lastBadOffset = -1;

  constructor(options: TlogReaderOptions = {}) {
    this.options = options;
  }

  /** 已消费的字节数（文件内的绝对偏移） */
  get position(): number {
    return this.base + this.cursor;
  }

  /** 流结束后的尾部信息（`end()` 之后才有值） */
  get incomplete(): TlogIncomplete | null {
    return this.tail;
  }

  /** 喂入一段字节，返回这次新解出的记录 */
  push(chunk: Uint8Array): TlogRecord[] {
    if (chunk.length > 0) {
      this.stats.bytesRead += chunk.length;
      this.ensureCapacity(this.length + chunk.length);
      this.buffer.set(chunk, this.length);
      this.length += chunk.length;
    }
    const records: TlogRecord[] = [];
    this.drain(records);
    this.compact();
    return records;
  }

  /**
   * 声明流已结束，收尾并返回尾部的不完整信息。
   *
   * 必须调用：只有到这一刻才能确定"读不满的那条记录"是文件被截断，而不是"还要等下一批数据"。
   */
  end(): TlogIncomplete | null {
    // 最后再切一次：这一轮之后不会再有数据，来不及成帧的部分就是"被截断的尾巴"
    this.drain([]);

    const remaining = this.length - this.cursor;
    if (remaining === 0) {
      this.tail = null;
      this.compact();
      return null;
    }

    // 尾部不足 9 字节时连"帧起点"都看不到，无法判定是不是垃圾；
    // 截断的尾巴里最常见的就是"只有时间戳的头几个字节"，按不完整记录报告更贴近事实。
    const frameStart = this.cursor + TLOG_RECORD_TIMESTAMP_BYTES;
    const looksLikeRecord =
      remaining < TLOG_RECORD_TIMESTAMP_BYTES + 1 || hasFrameStx(this.buffer, frameStart);

    if (!looksLikeRecord) {
      this.stats.droppedBytes += remaining;
      this.cursor = this.length;
      this.tail = null;
      this.compact();
      return null;
    }

    const timestampUs =
      remaining >= TLOG_RECORD_TIMESTAMP_BYTES ? readTimestampUs(this.buffer, this.cursor) : null;
    this.stats.incompleteBytes = remaining;
    this.tail = {
      bytes: remaining,
      timestampUs,
      preview: previewHex(this.buffer, this.cursor, remaining),
    };
    this.compact();
    return this.tail;
  }

  private ensureCapacity(needed: number): void {
    if (this.buffer.length >= needed) return;
    let size = this.buffer.length;
    while (size < needed) size *= 2;
    const grown = new Uint8Array(size);
    grown.set(this.buffer.subarray(0, this.length));
    this.buffer = grown;
  }

  /** 把已消费的前缀丢掉，缓冲区只保留未成帧的尾巴 */
  private compact(): void {
    if (this.cursor === 0) return;
    const remaining = this.length - this.cursor;
    if (remaining > 0) this.buffer.copyWithin(0, this.cursor, this.length);
    this.base += this.cursor;
    this.length = remaining;
    this.cursor = 0;
  }

  private drain(records: TlogRecord[]): void {
    for (;;) {
      const available = this.length - this.cursor;
      if (available === 0) return;

      // 连"时间戳 + 帧起点"都还没到齐：等下一批数据
      if (available < TLOG_RECORD_TIMESTAMP_BYTES + 1) return;

      const frameStart = this.cursor + TLOG_RECORD_TIMESTAMP_BYTES;
      if (!hasFrameStx(this.buffer, frameStart)) {
        const absolute = this.base + this.cursor;
        if (absolute !== this.lastBadOffset) {
          this.lastBadOffset = absolute;
          this.stats.badRecords += 1;
        }
        if (!this.resync()) return; // 数据不足以判定候选位置，等下一次 push / end()
        continue;
      }

      const frameLength = frameLengthOf(this.buffer, frameStart);
      if (frameLength === null) return; // 帧头还没到齐

      const recordLength = TLOG_RECORD_TIMESTAMP_BYTES + frameLength;
      if (available < recordLength) return; // 整条记录还没到齐

      const frame = this.buffer.slice(frameStart, frameStart + frameLength);
      const record: TlogRecord = {
        timestampUs: readTimestampUs(this.buffer, this.cursor),
        frame,
        offset: this.base + this.cursor,
      };

      const crc = frameCrcState(frame, false);
      if (crc === 'bad') this.stats.crcErrors += 1;
      else if (crc === 'unknown') this.stats.unknownMessages += 1;

      this.stats.records += 1;
      if (this.stats.firstTimestampUs === null) this.stats.firstTimestampUs = record.timestampUs;
      this.stats.lastTimestampUs = record.timestampUs;

      this.cursor += recordLength;
      records.push(record);
      this.options.onRecord?.(record);
    }
  }

  /**
   * 从当前位置往后找一个**能被验证的**记录起点（时间戳 + 一个 CRC 成立的帧）。
   *
   * 返回 false 表示"数据还不够判断"：此时不能前进，否则会把还没读到的真数据当成垃圾丢掉。
   * 代价是同一段字节会在多次 push 里被重复扫描 —— 但这段路只在文件损坏时才走到。
   */
  private resync(): boolean {
    const verify = this.options.resyncByCrc ?? true;
    for (let candidate = this.cursor + 1; candidate < this.length; candidate += 1) {
      const frameStart = candidate + TLOG_RECORD_TIMESTAMP_BYTES;
      if (frameStart >= this.length) return false;

      const frameLength = frameLengthOf(this.buffer, frameStart);
      if (frameLength === null) continue; // 可能是帧头没齐，也可能是垃圾：交给后面的候选
      if (this.length - frameStart < frameLength) continue;

      if (verify) {
        const frame = this.buffer.subarray(frameStart, frameStart + frameLength);
        if (frameCrcState(frame, true) !== 'ok') continue;
      }

      // 找到了新起点：中间的字节全部计入丢弃
      this.stats.droppedBytes += candidate - this.cursor;
      this.cursor = candidate;
      return true;
    }
    return false;
  }
}

export interface TlogReadResult {
  readonly records: readonly TlogRecord[];
  readonly stats: TlogStats;
  readonly incomplete: TlogIncomplete | null;
}

/** 一次性解析内存中的整份 `.tlog`（小文件、或已经全读进内存的编辑场景） */
export function readTlogBytes(bytes: Uint8Array, options: TlogReaderOptions = {}): TlogReadResult {
  const reader = new TlogReader(options);
  const records = reader.push(bytes);
  const incomplete = reader.end();
  return { records, stats: reader.stats, incomplete };
}

/** 流式解析（`ReadableStream` 的 `Symbol.asyncIterator` 可以直接喂进来） */
export async function readTlogStream(
  source: AsyncIterable<Uint8Array>,
  options: TlogReaderOptions = {},
): Promise<TlogReadResult> {
  const reader = new TlogReader(options);
  const records: TlogRecord[] = [];
  for await (const chunk of source) {
    for (const record of reader.push(chunk)) records.push(record);
  }
  const incomplete = reader.end();
  return { records, stats: reader.stats, incomplete };
}

/**
 * 边读边产出的版本。
 *
 * `reader` 由调用方持有：这样流结束后的统计与尾部信息不会随生成器一起被丢掉
 * （`for await` 会丢弃生成器的返回值，统计只能从 reader 上读）。
 */
export async function* iterateTlog(
  source: Uint8Array | AsyncIterable<Uint8Array>,
  reader: TlogReader,
): AsyncGenerator<TlogRecord, TlogStats, void> {
  if (source instanceof Uint8Array) {
    yield* reader.push(source);
  } else {
    for await (const chunk of source) {
      yield* reader.push(chunk);
    }
  }
  reader.end();
  return reader.stats;
}

/* -------------------------------------------------------------------------- */
/*  回放时钟                                                                    */
/* -------------------------------------------------------------------------- */

/** 取消一个已排定的回调 */
export type ReplayCancel = () => void;

/**
 * 回放用的时钟。做成可注入的，是因为"回放速度对不对"这件事只能用虚拟时钟严格断言：
 * 用真实时钟测只能测出"大致上像那么回事"，而变速写错的表现恰好就是"大致上像那么回事"。
 */
export interface ReplayClock {
  now(): number;
  schedule(delayMs: number, callback: () => void): ReplayCancel;
}

export const systemReplayClock: ReplayClock = {
  now: () => Date.now(),
  schedule: (delayMs, callback) => {
    const handle = setTimeout(callback, Math.max(0, delayMs));
    return () => {
      clearTimeout(handle);
    };
  },
};

/**
 * 测试与演示用的虚拟时钟：时间只在 `advance()` 时前进，到期的回调按时间顺序执行。
 *
 * `advance(0)` 会执行所有 0 延迟的回调（包括回调里新排定的），
 * 因此"尽快回放"在测试里是一条同步的确定性路径，而不是一串不确定的 `await`。
 */
export class VirtualClock implements ReplayClock {
  private currentMs: number;
  private sequence = 0;
  private readonly timers = new Map<number, { readonly at: number; readonly callback: () => void }>();

  constructor(startMs = 0) {
    this.currentMs = startMs;
  }

  now(): number {
    return this.currentMs;
  }

  schedule(delayMs: number, callback: () => void): ReplayCancel {
    this.sequence += 1;
    const id = this.sequence;
    this.timers.set(id, { at: this.currentMs + Math.max(0, delayMs), callback });
    return () => {
      this.timers.delete(id);
    };
  }

  /** 尚未触发的回调数。用于确认"暂停之后没有残留定时器"这类断言。 */
  get pending(): number {
    return this.timers.size;
  }

  advance(ms: number): void {
    const target = this.currentMs + Math.max(0, ms);
    for (;;) {
      let nextId = -1;
      let nextAt = Number.POSITIVE_INFINITY;
      for (const [id, timer] of this.timers) {
        if (timer.at <= target && timer.at < nextAt) {
          nextAt = timer.at;
          nextId = id;
        }
      }
      if (nextId === -1) break;
      const timer = this.timers.get(nextId);
      this.timers.delete(nextId);
      if (timer === undefined) continue;
      this.currentMs = Math.max(this.currentMs, timer.at);
      timer.callback();
    }
    this.currentMs = target;
  }
}

/* -------------------------------------------------------------------------- */
/*  回放播放器                                                                  */
/* -------------------------------------------------------------------------- */

/** 回放速度：`1/2/10` 等倍速，或 `'fast'`（尽快，不等时距） */
export type ReplaySpeed = number | 'fast';

/** 界面上的速度档位。放在这里而不是界面里：档位语义属于回放本身。 */
export const REPLAY_SPEED_PRESETS: readonly { readonly speed: ReplaySpeed; readonly label: string }[] =
  [
    { speed: 1, label: '1×' },
    { speed: 2, label: '2×' },
    { speed: 10, label: '10×' },
    { speed: 'fast', label: '尽快' },
  ];

/** 'fast' 模式每轮最多发出多少条：一次把几万条全发出去会把主线程锁住，界面会假死 */
const REPLAY_FAST_BATCH = 512;

export interface ReplayProgress {
  /** 已发出的记录条数，也是下一条待发记录的下标 */
  readonly index: number;
  readonly total: number;
  /** 最后一条已发出记录的时间戳 */
  readonly timestampUs: number | null;
  readonly firstTimestampUs: number | null;
  readonly lastTimestampUs: number | null;
  readonly finished: boolean;
}

export interface TlogPlayerOptions {
  readonly clock?: ReplayClock;
  readonly speed?: ReplaySpeed;
  /**
   * 传入的记录是否已经"全部到齐"。
   * 默认：构造时带了记录就算到齐；构造时为空则视为流式（之后靠 `append()` + `seal()`）。
   */
  readonly sealed?: boolean;
  readonly onFrame?: (frame: Uint8Array, record: TlogRecord) => void;
  /** 记录放完（且已 `seal()`）时调用一次 */
  readonly onEnd?: () => void;
}

/**
 * 按原始时距重放记录。
 *
 * 调度上只用**一个**定时器盯着"下一条记录到点了没有"：给每条记录各排一个定时器，
 * 在几万条的记录上就是几万个定时器，浏览器会先卡死再谈回放。
 *
 * 变速、暂停、跳转都只改"媒体时间与时钟的锚点"（`mediaBaseUs` / `clockBaseMs`），
 * 不动已经发出的记录下标 —— 这正是"不重复发帧、不漏帧"的来源。
 */
export class TlogPlayer {
  private readonly records: TlogRecord[] = [];
  private readonly clock: ReplayClock;
  private readonly onFrame: ((frame: Uint8Array, record: TlogRecord) => void) | undefined;
  private readonly onEnd: (() => void) | undefined;

  private speedValue: ReplaySpeed;
  private index = 0;
  private playing = false;
  private cancelTimer: ReplayCancel | null = null;
  /** 已消费的媒体时间（µs，相对第一条记录） */
  private mediaBaseUs = 0;
  /** 与 `mediaBaseUs` 对应的时钟刻度 */
  private clockBaseMs = 0;
  private originUs = 0;
  private hasOrigin = false;
  private sealedFlag = false;
  private endReported = false;

  constructor(records: readonly TlogRecord[] = [], options: TlogPlayerOptions = {}) {
    this.clock = options.clock ?? systemReplayClock;
    this.speedValue = options.speed ?? 1;
    this.onFrame = options.onFrame;
    this.onEnd = options.onEnd;
    this.append(records);
    if (options.sealed ?? records.length > 0) this.seal();
  }

  get speed(): ReplaySpeed {
    return this.speedValue;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  /** 是否已经声明"不会再有新记录" */
  get sealed(): boolean {
    return this.sealedFlag;
  }

  /** 已发出的记录条数（也是下一条待发记录的下标） */
  get position(): number {
    return this.index;
  }

  get total(): number {
    return this.records.length;
  }

  get finished(): boolean {
    return this.records.length > 0 && this.sealedFlag && this.index >= this.records.length;
  }

  progress(): ReplayProgress {
    const last = this.records[this.records.length - 1];
    return {
      index: this.index,
      total: this.records.length,
      timestampUs: this.index === 0 ? null : (this.records[this.index - 1]?.timestampUs ?? null),
      firstTimestampUs: this.records[0]?.timestampUs ?? null,
      lastTimestampUs: last?.timestampUs ?? null,
      finished: this.finished,
    };
  }

  /** 流式回放：追加记录。追加意味着"还没完"，因此会清掉 sealed 标记（放完后需要重新 `seal()`）。 */
  append(records: readonly TlogRecord[]): void {
    if (records.length === 0) return;
    /*
     * 之前已经报过"放完了"，现在又来了新记录 —— 那次结束是提前的。
     * 流式解析追不上回放速度时就会出现这种情况，此时必须接着放，
     * 否则回放会停在半路再也不动（看起来像卡死，其实只是没人再推它）。
     */
    const resume = this.endReported;
    if (!this.hasOrigin) {
      this.originUs = records[0]?.timestampUs ?? 0;
      this.hasOrigin = true;
    }
    for (const record of records) this.records.push(record);
    this.sealedFlag = false;
    this.endReported = false;
    if (this.index >= this.records.length) return;
    if (this.playing) {
      if (this.cancelTimer === null) this.scheduleNext(0);
      return;
    }
    if (resume) this.play();
  }

  /** 声明不会再有新记录了。只有放完最后一条之后才会报"结束"。 */
  seal(): void {
    this.sealedFlag = true;
    this.reportEndIfDone();
  }

  /**
   * 回到开头并停下（重新打开一条回放通道、或从头重放时用）。
   *
   * 不复用 `seek(0)`：`seek` 对"已经放到底"的情况会自动继续播放，而这里要的是一个
   * 明确的、不带副作用的复位 —— 打开通道的人接下来会自己决定要不要播。
   */
  rewind(): void {
    this.playing = false;
    this.clearTimer();
    this.index = 0;
    this.mediaBaseUs = 0;
    this.clockBaseMs = this.clock.now();
    this.endReported = false;
  }

  play(): void {
    if (this.playing) return;
    // 空记录：没有东西可放，也不该对外说"正在播放"（界面会一直转圈）
    if (this.records.length === 0 && this.sealedFlag) return;
    if (this.finished) this.rewind(); // 已经放到底了再按播放，从头再来一遍
    this.playing = true;
    this.endReported = false;
    this.clockBaseMs = this.clock.now();
    if (this.index < this.records.length) this.scheduleNext(0);
    // 没有记录（还在流式解析）时保持"播放中"：append() 会把第一条立刻安排下去
  }

  pause(): void {
    if (!this.playing) return;
    this.mediaBaseUs = this.mediaNow();
    this.playing = false;
    this.clearTimer();
  }

  setSpeed(speed: ReplaySpeed): void {
    if (speed !== 'fast' && (!Number.isFinite(speed) || speed <= 0)) {
      throw new RangeError(`回放速度必须是正数或 'fast'，收到 ${String(speed)}`);
    }
    if (speed === this.speedValue) return;
    // 先按旧速度把"已经走了多少媒体时间"结算掉，再换挡 ——
    // 否则变速那一瞬间媒体时间会跳一下，看起来就是"跳帧"。
    this.mediaBaseUs = this.mediaNow();
    this.clockBaseMs = this.clock.now();
    this.speedValue = speed;
    if (this.playing) this.scheduleNext(0);
  }

  /**
   * 跳到第 `index` 条记录（含），从它开始继续。跳过去的记录不会被补发，也不会重复发。
   *
   * 播放中的跳转立即生效；暂停中的跳转保持暂停（等用户按播放）；
   * 已经放到底之后再跳，则视为"想从那里继续看"，自动重新开始播放。
   */
  seek(index: number): void {
    const resume = !this.playing && this.finished; // 必须在改动下标之前判断
    const clamped = Math.max(0, Math.min(Math.trunc(index), this.records.length));
    this.index = clamped;
    // 目标记录视为"已经到点"，所以下一次 tick 会立刻发它，其后的记录仍保持原始时距
    this.mediaBaseUs = this.dueUs(clamped);
    this.clockBaseMs = this.clock.now();
    this.endReported = false;
    this.clearTimer();
    if (this.playing) this.scheduleNext(0);
    else if (resume) this.play();
  }

  /** 跳到第一条不早于 `timestampUs` 的记录；都早于目标时跳到末尾。 */
  seekToTimestamp(timestampUs: number): void {
    let target = this.records.length;
    for (let index = 0; index < this.records.length; index += 1) {
      const record = this.records[index];
      if (record !== undefined && record.timestampUs >= timestampUs) {
        target = index;
        break;
      }
    }
    this.seek(target);
  }

  private dueUs(index: number): number {
    const record = this.records[index];
    if (record !== undefined) return record.timestampUs - this.originUs;
    const last = this.records[this.records.length - 1];
    return last === undefined ? 0 : last.timestampUs - this.originUs;
  }

  private mediaNow(): number {
    if (!this.playing) return this.mediaBaseUs;
    if (this.speedValue === 'fast') return this.dueUs(this.index);
    return this.mediaBaseUs + (this.clock.now() - this.clockBaseMs) * this.speedValue * 1000;
  }

  private scheduleNext(delayMs: number): void {
    this.clearTimer();
    this.cancelTimer = this.clock.schedule(delayMs, () => {
      this.cancelTimer = null;
      this.tick();
    });
  }

  private clearTimer(): void {
    const cancel = this.cancelTimer;
    if (cancel === null) return;
    this.cancelTimer = null;
    cancel();
  }

  private tick(): void {
    if (!this.playing) return;

    const speed = this.speedValue;
    const fast = speed === 'fast';
    const mediaUs = this.mediaNow();
    let emitted = 0;

    for (;;) {
      // 回调里可能按了暂停或跳转（界面就是这么用的）：一旦不再播放就立刻收手
      if (!this.playing) return;
      const record = this.records[this.index];
      if (record === undefined) break;
      if (!fast && record.timestampUs - this.originUs > mediaUs) break;
      this.index += 1;
      this.onFrame?.(record.frame, record);
      emitted += 1;
      // "尽快"也不能把几万条帧塞进同一次 tick：界面要能刷新，暂停按钮要能按得动
      if (fast && emitted >= REPLAY_FAST_BATCH) break;
    }

    if (this.index >= this.records.length) {
      this.reportEndIfDone();
      // 还有记录没解析进来时**不排定时器**：空转没有意义，append() 会重新安排
      return;
    }

    if (fast) {
      this.scheduleNext(0);
      return;
    }
    const next = this.records[this.index];
    if (next === undefined) return;
    const remainingUs = next.timestampUs - this.originUs - this.mediaNow();
    this.scheduleNext(Math.max(0, remainingUs / 1000 / speed));
  }

  private reportEndIfDone(): void {
    if (this.endReported) return;
    if (!this.sealedFlag || this.records.length === 0 || this.index < this.records.length) return;
    this.endReported = true;
    // 停下之前把媒体时间锚点结算清楚：之后再 play() 或 append() 才是"从停下处继续"，
    // 而不是拿着过期的锚点把时间轴往回拨一下
    this.mediaBaseUs = this.mediaNow();
    this.playing = false;
    this.clearTimer();
    this.onEnd?.();
  }
}
